import { useState, useRef, useEffect, useCallback, MouseEvent as ReactMouseEvent } from 'react';

import {
  LUBAV_PANEL_DEFAULT_WIDTH_PCT,
  LUBAV_PANEL_MIN_WIDTH_PX,
} from '@backrs_ui/grow-ui/src/components/Lubav/ResizableDivider';

const MAX_WIDTH_PCT = 50;
const SNAP_POINTS = 16;
const DIVIDER_HALF_WIDTH_PX = 24;
const EXPAND_DRAG_THRESHOLD_PX = 5;

const pxToPct = (px: number, containerWidth: number): number =>
  containerWidth > 0 ? (px / containerWidth) * 100 : 0;

// Percentage widths resolve against the container's content box, so padding is excluded here.
const getContentBox = (containerId: string): { left: number; width: number } => {
  const container = document.getElementById(containerId);

  if (!container) return { left: 0, width: 0 };

  const rect = container.getBoundingClientRect();
  const { paddingLeft, paddingRight } = window.getComputedStyle(container);
  const padLeft = parseFloat(paddingLeft) || 0;
  const padRight = parseFloat(paddingRight) || 0;

  return { left: rect.left + padLeft, width: container.clientWidth - padLeft - padRight };
};

const getMinWidthPct = (containerId: string): number =>
  pxToPct(LUBAV_PANEL_MIN_WIDTH_PX, getContentBox(containerId).width);

const clampLeftWidthPct = (pct: number, containerId: string): number => {
  const minPct = getMinWidthPct(containerId);

  return Math.min(Math.max(pct, minPct), MAX_WIDTH_PCT);
};

type DragMode =
  | { kind: 'idle' }
  | { kind: 'resize'; wantsToCollapse: boolean }
  | { kind: 'expand'; startX: number; didDrag: boolean };

type UseResizableDividerProps = Readonly<{
  containerId: string;
  isMobile: boolean;
  initialWidth?: number;
}>;

type UseResizableDividerReturn = Readonly<{
  leftWidth: number;
  isResizing: boolean;
  isCollapsed: boolean;
  handleMouseDown: () => void;
  handleExpandMouseDown: (e: ReactMouseEvent) => void;
  expand: () => void;
}>;

export const useResizableDivider = ({
  containerId,
  isMobile,
  initialWidth,
}: UseResizableDividerProps): UseResizableDividerReturn => {
  const [leftWidth, setLeftWidth] = useState(() =>
    clampLeftWidthPct(initialWidth ?? LUBAV_PANEL_DEFAULT_WIDTH_PCT, containerId),
  );
  const [isResizing, setIsResizing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const prevInitialWidth = useRef(initialWidth);
  const dragMode = useRef<DragMode>({ kind: 'idle' });

  const expand = useCallback(() => {
    setIsCollapsed(false);
    setLeftWidth(clampLeftWidthPct(initialWidth ?? LUBAV_PANEL_DEFAULT_WIDTH_PCT, containerId));
  }, [containerId, initialWidth]);

  // When the route's default changes, reset to the new default
  // unless the user has manually dragged to a different position.
  useEffect(() => {
    if (initialWidth == null || initialWidth === prevInitialWidth.current) return;

    if (leftWidth === prevInitialWidth.current) {
      setLeftWidth(clampLeftWidthPct(initialWidth, containerId));
    }

    prevInitialWidth.current = initialWidth;
  }, [initialWidth, leftWidth, containerId]);

  const handleMouseDown = () => {
    if (isMobile) return;

    dragMode.current = { kind: 'resize', wantsToCollapse: false };
    setIsResizing(true);
  };

  const handleExpandMouseDown = (e: ReactMouseEvent) => {
    if (isMobile) return;

    dragMode.current = { kind: 'expand', startX: e.clientX, didDrag: false };
    setIsResizing(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const mode = dragMode.current;

      if (mode.kind === 'idle') return;

      const contentBox = getContentBox(containerId);

      if (contentBox.width <= 0) return;

      const minWidth = pxToPct(LUBAV_PANEL_MIN_WIDTH_PX, contentBox.width);
      const rawWidth = ((e.clientX - contentBox.left) / contentBox.width) * 100;
      const offsetPct = (DIVIDER_HALF_WIDTH_PX / contentBox.width) * 100;
      const newWidth = rawWidth - offsetPct;

      if (mode.kind === 'expand') {
        if (Math.abs(e.clientX - mode.startX) < EXPAND_DRAG_THRESHOLD_PX) return;

        if (!mode.didDrag) {
          mode.didDrag = true;
          setIsCollapsed(false);
        }

        // Track the cursor from the edge so it drags open; the min width is enforced on release.
        setLeftWidth(Math.min(Math.max(rawWidth, 0), MAX_WIDTH_PCT));

        return;
      }

      if (newWidth < minWidth) {
        mode.wantsToCollapse = true;
        setLeftWidth(minWidth);
      } else if (newWidth <= MAX_WIDTH_PCT) {
        mode.wantsToCollapse = false;
        setLeftWidth(newWidth);
      }
    },
    [containerId],
  );

  const handleMouseUp = useCallback(() => {
    const snapToGrid = () =>
      setLeftWidth((prev) => {
        const minWidth = getMinWidthPct(containerId);
        const step = (MAX_WIDTH_PCT - minWidth) / (SNAP_POINTS - 1);
        const snapped = Math.round((prev - minWidth) / step) * step + minWidth;

        return Math.min(Math.max(snapped, minWidth), MAX_WIDTH_PCT);
      });

    const mode = dragMode.current;

    if (mode.kind === 'expand') {
      // Drag-only reopen: a bare click leaves it collapsed (click reopens via the home item).
      if (mode.didDrag) snapToGrid();
    } else if (mode.kind === 'resize') {
      if (mode.wantsToCollapse) setIsCollapsed(true);
      else snapToGrid();
    }

    dragMode.current = { kind: 'idle' };
    setIsResizing(false);
  }, [containerId]);

  useEffect(() => {
    if (isMobile) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMobile, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (isResizing) {
      document.body.style.userSelect = 'none';

      return;
    }

    const timeout = window.setTimeout(() => {
      document.body.style.userSelect = '';
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [isResizing]);

  return {
    leftWidth,
    isResizing,
    isCollapsed,
    handleMouseDown,
    handleExpandMouseDown,
    expand,
  };
};
